import {
  TextField, Autocomplete, Box, Card, CardContent, IconButton, LinearProgress,
  Stack, Typography, Link, Paper, Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";
import { blue } from "@mui/material/colors";
import { FormEvent, SyntheticEvent, useEffect, useMemo, useState, useCallback } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from '@mui/icons-material/Close';
import PermDeviceInformationIcon from '@mui/icons-material/PermDeviceInformation';
import debounce from "lodash/debounce";
import { RetractedAuthor } from "./CommonInterfaces";
import { scrollToSection } from "../ScrollUtils";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import AuthorHeader from "./author/AuthorHeader";
import TabbedDrillDown from "./author/TabbedDrillDown";

interface AdvancedAuthorSearchProps {
  authorSearchRequested: string | null
}

function AdvancedAuthorSearch({ authorSearchRequested }: AdvancedAuthorSearchProps) {
  const [inputValue, setInputValue] = useState('');
  const [validOptionSelected, setValidOptionSelected] = useState(false);
  const [possibleValues, setPossibleValues] = useState<string[]>([]);
  const [retractedAuthors, setRetractedAuthors] = useState<RetractedAuthor[]>([]);
  const [filteredAuthors, setFilteredAuthors] = useState<RetractedAuthor[]>([]);
  const [availableInstitutions, setAvailableInstitutions] = useState<string[]>([]);
  const [institutionFilter, setInstitutionFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResultsAvailable, setSearchResultsAvailable] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [inspireMe, setInspireMe] = useState(false);
  const [showError, setShowError] = useState(false);
  const [choosenAuthor, setChoosenAuthor] = useState<RetractedAuthor | undefined>(undefined);
  const [authorChoosenSource, setAuthorChoosenSource] = useState<string>();
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const [multiMode, setMultiMode] = useState(false);
  const [authorLines, setAuthorLines] = useState('');
  const [multipleSearchQuery, setMultipleSearchQuery] = useState('');
  const [disambiguationMap, setDisambiguationMap] = useState<Record<string, RetractedAuthor[]>>({});
  const [resolvedAuthors, setResolvedAuthors] = useState<RetractedAuthor[]>([]);
  const [authorsWithNoResults, setAuthorsWithNoResults] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState<RetractedAuthor[]>([]);
  const [dialogSearchTerm, setDialogSearchTerm] = useState('');

  const fetchSuggestions = useCallback(async (data: string) => {
    if (data.length > 3 && !data.startsWith("https://orcid.org") && !data.startsWith("http://orcid.org")) {
      try {
        const response = await fetch(`${argosApiEntryPoint}/retraction/author/suggest/${encodeURIComponent(data)}`);
        if (response.ok) {
          const suggestions = await response.json();
          setPossibleValues(suggestions);
        } else {
          setPossibleValues([]);
        }
      } catch {
        setPossibleValues([]);
      }
    }
  }, []);

  const debouncedFetchSuggestions = useMemo(() => debounce(fetchSuggestions, 200), [fetchSuggestions]);

  const handleInputChange = useCallback((e: SyntheticEvent, value: string) => {
    setInputValue(value);
    setValidOptionSelected(false);
    if (value.length > 3) {
      setAutocompleteOpen(true);
      debouncedFetchSuggestions(value);
    } else {
      setAutocompleteOpen(false);
      setPossibleValues([]);
    }
  }, [debouncedFetchSuggestions]);

  const searchSingleAuthor = async function (query: string): Promise<RetractedAuthor[]> {
    const authToken = await auth.currentUser?.getIdToken(true);
    const response = await fetch(
      (query.startsWith("https://orcid.org") || query.startsWith("http://orcid.org")) ?
        `${argosApiEntryPoint}/retraction/author/orcid/${query.replaceAll("https://orcid.org/", "").replaceAll("http://orcid.org/", "")}`
        :
        `${argosApiEntryPoint}/retraction?search=name:${query}`
      , {
        method: 'GET',
        headers: {
          'content-type': 'application/json;charset=UTF-8',
          'Authorization': `Bearer ${authToken}`
        }
      });

    if (response.ok) {
      return response.json();
    } else {
      throw new Error("Unable to search author, please try again or email hello@scitility.com for support")
    }
  };


  const searchRetractions = useCallback(async (query: string) => {
    setSearchResultsAvailable(false);
    setSearchInProgress(true);
    setShowError(false);

    try {
      const authors: RetractedAuthor[] = await searchSingleAuthor(query);
      setRetractedAuthors(authors);
      setFilteredAuthors(authors);

      const institutions = Array.from(new Set(
        authors.flatMap(author => author.institutions || [])
      ));
      setAvailableInstitutions(institutions.sort());
      setInstitutionFilter(null);

      setSearchResultsAvailable(true);

    } catch {
      setShowError(true);
    } finally {
      setSearchInProgress(false);
    }
  }, []);

  const handleMultiSearch = useCallback(async () => {
    setSearchInProgress(true);
    setAuthorsWithNoResults([]);
    setResolvedAuthors([]);
    setDisambiguationMap({});
    setMultipleSearchQuery(authorLines)
    const names = authorLines.split('\n').map(s => s.trim()).filter(Boolean);
    const disambiguations: Record<string, RetractedAuthor[]> = {};
    const authorsWithNoResults: string[] = []
    const resolved: RetractedAuthor[] = [];
    for (const name of names) {
      const authors = await searchSingleAuthor(name);
      if (authors.length === 1) {
        resolved.push(authors[0]);
      } else if (authors.length === 0) {
        authorsWithNoResults.push(name)
      } else if (authors.length > 1) {
        disambiguations[name] = authors;
      }
    }
    setResolvedAuthors(resolved);
    setDisambiguationMap(disambiguations);
    setAuthorsWithNoResults(authorsWithNoResults)
    setSearchInProgress(false);
  }, [authorLines, searchSingleAuthor]);

  const openDialog = (name: string, authors: RetractedAuthor[]) => {
    setDialogSearchTerm(name);
    setDialogOptions(authors);
    setDialogOpen(true);
  };

  const handleDialogSelect = (author: RetractedAuthor) => {
    setResolvedAuthors(prev => [...prev, author]);
    setDisambiguationMap(prev => {
      const { [dialogSearchTerm]: _, ...rest } = prev;
      return rest;
    });
    setDialogOpen(false);
  };

  const renderDisambiguationCard = (name: string, authors: RetractedAuthor[]) => (
    <Box
      onClick={() => openDialog(name, authors)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          openDialog(name, authors);
        }
      }}
      sx={{
        cursor: 'pointer',
        p: 1,
        borderRadius: 1,
        '&:hover': { backgroundColor: '#e6ecf3' },
        '&:focus-visible': { boxShadow: '0 0 0 2px #66a3ff' }
      }}
    >
      <Paper sx={{ margin: 2, minHeight: 150 }}>
        <CardContent sx={{ alignContent: 'initial' }} key={'author_' + name}>
          <Typography variant="subtitle1">Multiple matches found for "{name}"</Typography>
          <Typography variant="body2" color="text.secondary">Click to resolve this author to the correct profile.</Typography>
        </CardContent>
      </Paper>
    </Box>
  );

  const renderNoResultCard = (name: string) => (
    <Box
      sx={{
        cursor: 'pointer',
        p: 1,
        borderRadius: 1,
        '&:hover': { backgroundColor: '#e6ecf3' },
        '&:focus-visible': { boxShadow: '0 0 0 2px #66a3ff' }
      }}
    >
      <Paper sx={{ margin: 2, minHeight: 150 }}>
        <CardContent sx={{ alignContent: 'initial' }} key={'author_' + name}>
          <Typography variant="subtitle1">We are not able to find any retraction, expression of concern or correction for "{name}"</Typography>
        </CardContent>
      </Paper>
    </Box>
  );

  const handleAuthorSelected = useCallback((e: SyntheticEvent, value: string | null) => {
    if (value && value !== searchQuery) {
      setValidOptionSelected(true);
      setAutocompleteOpen(false);
      setSearchQuery(value);
      setInputValue(value);
      setRetractedAuthors([]);
      searchRetractions(value);
    }
  }, [searchQuery, searchRetractions]);

  const handleInstitutionFilterChange = (event: SyntheticEvent, value: string | null) => {
    setInstitutionFilter(value);
    if (value) {
      const filtered = retractedAuthors.filter(author =>
        author.institutions?.includes(value)
      );
      setFilteredAuthors(filtered);
    } else {
      setFilteredAuthors(retractedAuthors);
    }
  };

  useEffect(() => {
    if (authorSearchRequested && authorSearchRequested.length > 3 && !searchResultsAvailable) {
      setValidOptionSelected(true);
      setSearchQuery(authorSearchRequested);
      setInputValue(authorSearchRequested);
      setRetractedAuthors([]);
      searchRetractions(authorSearchRequested);
    }
  }, [authorSearchRequested, searchResultsAvailable, searchRetractions]);

  useEffect(() => {
    if (searchResultsAvailable) {
      scrollToSection("author-search-result");
    }
  }, [searchResultsAvailable]);

  useEffect(() => {
    return () => debouncedFetchSuggestions.cancel();
  }, [debouncedFetchSuggestions]);

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inputValue && inputValue.length > 3) {
      setAutocompleteOpen(false);
      setSearchQuery(inputValue);
      setInputValue(inputValue);
      setRetractedAuthors([]);
      setPossibleValues([]);
      searchRetractions(inputValue);
    }
  }, [inputValue, searchRetractions]);

  return (
    <>
      <div id="author-analysis" />
      <Typography variant="h3" gutterBottom sx={{ color: '#003366', fontWeight: 'bold' }}>
        Advanced Author analysis
      </Typography>

      {choosenAuthor === undefined && !multiMode && (
        <>
          <Autocomplete
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleAuthorSelected}
            options={possibleValues}
            getOptionLabel={(option) => option}
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            renderInput={(params) => (
              <Card variant="outlined">
                <CardContent>
                  <form onSubmit={handleSubmit}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 4 }}>
                      <TextField
                        {...params}
                        label="Search retractions by author name / ORCID"
                        variant="outlined"
                        helperText="Start typing an author's name (min. 4 characters) or paste a full ORCID (eg: https://orcid.org/0000-1111-2222-3333)"
                      />
                      <IconButton type="submit" aria-label="search">
                        <SearchIcon sx={{ color: blue, fontSize: 40 }} />
                      </IconButton>
                    </Stack>
                  </form>

                  {!inspireMe &&
                    <Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
                      <Link onClick={() => setInspireMe(true)}>
                        <Typography variant="body2" component="div" sx={{ mb: 1 }}>Looking for inspiration?</Typography>
                      </Link>
                      <Link onClick={() => setMultiMode(true)}>
                        <Typography variant="body2">Check for multiple authors at once</Typography>
                      </Link>
                    </Box>}
                  {inspireMe &&
                    <><Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
                      <Typography variant="body2" component="div" sx={{ mb: 1 }}>Looking for inspiration? You can try the following authors:</Typography>
                      <Link onClick={() => setInputValue('Shamshirband')}><Typography variant="caption" component="div">Shamshirband</Typography></Link>
                      <Link onClick={() => setInputValue('Jose L Calvo-Guirado')}><Typography variant="caption" component="div">Jose L Calvo-Guirado</Typography></Link>
                      <Link onClick={() => setInputValue('Vladimir Anatolevich Parfenov')}><Typography variant="caption" component="div">Vladimir Anatolevich Parfenov</Typography></Link>
                    </Box>
                      <Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
                        <Link onClick={() => setMultiMode(true)}>
                          <Typography variant="caption">Check for multiple authors at once</Typography>
                        </Link>
                      </Box>
                    </>}
                </CardContent>
              </Card>
            )}
          />

          {searchInProgress && <LinearProgress />}

          {showError && (
            <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Stack direction="row" alignItems="center">
                <PermDeviceInformationIcon color="error" sx={{ ml: 1 }} />
                <Typography variant="h6" color="error" sx={{ ml: 2 }}>
                  Sorry, there was an error trying to run your search
                </Typography>
              </Stack>
            </Card>
          )}

          {searchResultsAvailable && (
            <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Stack direction="row">
                <Box flexGrow={1}>
                  {retractedAuthors.length > 0 &&
                    <>
                      <div id="author-search-result" />
                      <Typography variant="caption">
                        Search results for {searchQuery} {institutionFilter && ' at ' + institutionFilter} – {filteredAuthors.length} matching author(s)
                      </Typography>
                    </>
                  }
                  {retractedAuthors.length === 0 &&
                    <Stack direction="row"><PermDeviceInformationIcon color="primary" sx={{ ml: 4 }} />
                      <Typography gutterBottom variant="h6" component="div" sx={{ ml: 2, mb: 2 }} color="primary">
                        Good news: We are not able to find any retraction, expression of concern or correction for {searchQuery}
                      </Typography>
                    </Stack>
                  }
                </Box>
                <IconButton onClick={() => setSearchResultsAvailable(false)}>
                  <CloseIcon />
                </IconButton>
              </Stack>

              {retractedAuthors.length > 1 && availableInstitutions.length > 0 && (
                <Box sx={{ my: 2 }}>
                  <Autocomplete
                    value={institutionFilter}
                    onChange={handleInstitutionFilterChange}
                    options={availableInstitutions}
                    renderInput={(params) => (
                      <TextField {...params} label="Filter by institution" variant="outlined" />
                    )}
                    clearOnEscape
                  />
                </Box>
              )}

              <Grid container spacing={2}>
                {filteredAuthors.map((element) => (
                  <Grid item sm={filteredAuthors.length === 1 ? 12 : 6} key={element.ids.openAlexId}>
                    <Box
                      onClick={() => { setChoosenAuthor(element); setAuthorChoosenSource("single") }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setChoosenAuthor(element);
                          setAuthorChoosenSource("single");
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#e6ecf3' },
                        '&:focus-visible': { boxShadow: '0 0 0 2px #66a3ff' }
                      }}
                    >
                      <AuthorHeader author={element} index={0} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          )}
        </>
      )}

      {choosenAuthor && (
        <Paper sx={{ m: 2 }}>
          <Stack direction="row">
            <Box flexGrow={1}>
              <div id="author-search-result" />
              <Typography variant="caption">
                Close to go back to the search results for {authorChoosenSource === 'single'? searchQuery : multipleSearchQuery.split('\n').join(', ')}
              </Typography>
            </Box>
            <IconButton onClick={() => { setChoosenAuthor(undefined); setAuthorChoosenSource(undefined) }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <AuthorHeader author={choosenAuthor} index={999} />
          <TabbedDrillDown authorId={choosenAuthor.ids.openAlexId} />
          <Box sx={{ paddingBottom: 2 }} />
        </Paper>
      )}

      {!choosenAuthor && multiMode && (
        <>
          <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 4 }}>
              <TextField
                size="small"
                label="Enter one author name or ORCID per line"
                multiline
                minRows={5}
                value={authorLines}
                onChange={(e) => setAuthorLines(e.target.value)}
                fullWidth
                sx={{
                  '& .MuiInputBase-inputMultiline': {
                    fontSize: '0.8rem', // or 12px, etc.
                  },
                }}
              />
              <IconButton onClick={handleMultiSearch}>
                <SearchIcon sx={{ color: blue, fontSize: 40 }} />
              </IconButton>
            </Stack>


            <Stack direction="row" spacing={2} marginTop={2}>
              <Link onClick={() => {
                setInspireMe(false)
                setMultiMode(false)
              }}>
                <Typography variant="body2">Switch back to single author search</Typography>
              </Link>
            </Stack>

          </Card>

          {searchInProgress && <LinearProgress />}

          {!searchInProgress &&
            (resolvedAuthors.length > 0 || authorsWithNoResults.length > 0 || Object.keys(disambiguationMap).length > 0)
            &&
            <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Stack direction="row">
                <Box flexGrow={1}>
                  {authorLines.length > 0 &&
                    <>
                      <div id="author-search-result" />
                      <Typography variant="caption">
                        Search results for {multipleSearchQuery.split("\n").join(", ")}
                      </Typography>
                    </>
                  }
                </Box>
              </Stack>

              <Grid container spacing={2}>
                {resolvedAuthors.map((element, idx) => (
                  <Grid item sm={6} key={element.ids.openAlexId}>
                    <Box
                      onClick={() => { setChoosenAuthor(element); setAuthorChoosenSource("multiple"); }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setChoosenAuthor(element);
                          setAuthorChoosenSource("multiple");
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#e6ecf3' },
                        '&:focus-visible': { boxShadow: '0 0 0 2px #66a3ff' }
                      }}
                    >
                      <AuthorHeader author={element} index={idx} />
                    </Box>
                  </Grid>
                ))}
                {Object.entries(disambiguationMap).map(([name, authors]) =>
                  <Grid item sm={6} key={name}>
                    {renderDisambiguationCard(name, authors)}
                  </Grid>)
                }
                {authorsWithNoResults.map(name =>
                  <Grid item sm={6} key={name}>
                    {renderNoResultCard(name)}
                  </Grid>)
                }
              </Grid>
            </Card>
          }



          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
            <DialogTitle>Resolve "{dialogSearchTerm}"</DialogTitle>
            <DialogContent>
              {dialogOptions.map((author, idx) => (
                <Box key={idx} sx={{ p: 1, borderBottom: '1px solid #ccc', cursor: 'pointer' }}
                  onClick={() => handleDialogSelect(author)}>
                  <AuthorHeader author={author} index={idx} />
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </>



      )}



    </>
  );
}

export default AdvancedAuthorSearch;
